import { Navigate, useRoutes } from 'react-router-dom';
// auth
import AuthGuard from '../auth/AuthGuard';
import GuestGuard from '../auth/GuestGuard';
// layouts
import MainLayout from '../layouts/main';
import SimpleLayout from '../layouts/simple';
import CompactLayout from '../layouts/compact';
import DashboardLayout from '../layouts/dashboard';
// config
import { PATH_AFTER_LOGIN } from '../config-global';
//
import {
  // Auth
  LoginPage,
  RegisterPage,
  VerifyCodePage,
  NewPasswordPage,
  ResetPasswordPage,
  Login,
  SignUp,
  // Dashboard: General
  GeneralAppPage,
  GamesPage,
  GamePage,
  AddGamePage,
  AdminPanel,
  GeneralFilePage,
  GeneralBankingPage,
  GeneralBookingPage,
  GeneralEcommercePage,
  GeneralAnalyticsPage,
  CalendarPage,
  // Dashboard: User
  UserListPage,
  UserEditPage,
  UserCardsPage,
  UserCreatePage,
  UserProfilePage,
  UserAccountPage,
  // Dashboard: Ecommerce
  EcommerceShopPage,
  EcommerceCheckoutPage,
  EcommerceProductListPage,
  EcommerceProductEditPage,
  EcommerceProductCreatePage,
  EcommerceProductDetailsPage,
  // Dashboard: Invoice
  InvoiceListPage,
  InvoiceDetailsPage,
  InvoiceCreatePage,
  InvoiceEditPage,
  // Dashboard: Blog
  BlogPostsPage,
  BlogPostPage,
  BlogNewPostPage,
  // Dashboard: FileManager
  FileManagerPage,
  // Dashboard: App
  ChatPage,
  MailPage,
  KanbanPage,
  //
  BlankPage,
  PermissionDeniedPage,
  //
  Page500,
  Page403,
  Page404,
  HomePage,
  FaqsPage,
  AboutPage,
  Contact,
  PricingPage,
  PaymentPage,
  ComingSoonPage,
  MaintenancePage,
  TermsAndConditionsPage,
  //
  ComponentsOverviewPage,
  FoundationColorsPage,
  FoundationTypographyPage,
  FoundationShadowsPage,
  FoundationGridPage,
  FoundationIconsPage,
  //
  MUIAccordionPage,
  MUIAlertPage,
  MUIAutocompletePage,
  MUIAvatarPage,
  MUIBadgePage,
  MUIBreadcrumbsPage,
  MUIButtonsPage,
  MUICheckboxPage,
  MUIChipPage,
  MUIDataGridPage,
  MUIDialogPage,
  MUIListPage,
  MUIMenuPage,
  MUIPaginationPage,
  MUIPickersPage,
  MUIPopoverPage,
  MUIProgressPage,
  MUIRadioButtonsPage,
  MUIRatingPage,
  MUISliderPage,
  MUIStepperPage,
  MUISwitchPage,
  MUITablePage,
  MUITabsPage,
  MUITextFieldPage,
  MUITimelinePage,
  MUITooltipPage,
  MUITransferListPage,
  MUITreesViewPage,
  //
  DemoAnimatePage,
  DemoCarouselsPage,
  DemoChartsPage,
  DemoCopyToClipboardPage,
  DemoEditorPage,
  DemoFormValidationPage,
  DemoImagePage,
  DemoLabelPage,
  DemoLightboxPage,
  DemoMapPage,
  DemoMegaMenuPage,
  DemoMultiLanguagePage,
  DemoNavigationBarPage,
  DemoOrganizationalChartPage,
  DemoScrollbarPage,
  DemoSnackbarPage,
  DemoTextMaxLinePage,
  DemoUploadPage,
  DemoMarkdownPage,
  EventsPage,
  EventPage,
  AddEventPage,
  AddPrizePage,
  ProvidersPage,
  AddProvider,
  Discord,
  ForgotPassword,
  MyEventsPage,
  PreviewEventDiscord,
  EventRegistrationsListPage,
  AdminEventsPage,
  AdminGamesPage,
  HomepageUploads,
  ProviderUserListPage,
  OAuthLogin,
  AddGLDSProviderPage,
  GLDSProvidersPage,
  AdTranLanding,
  MarketingPage,
} from './elements';
import NoIndex from 'src/components/noindex/NoIndex';
import ClashLanding from 'src/pages/FGNClashLanding';
import ClashSignUp from 'src/pages/ClashSignUp';
import IowaClashLanding from 'src/pages/IowaClashLanding';
import IowaClashFreeAgentSignup from 'src/pages/ClashFreeAgentSignUp';
import KansasClashLanding from 'src/pages/KansasClashLanding';
import KansasClashFreeAgentSignup from 'src/pages/KansasClashFreeAgentSignUp';
import KansasClashSignUp from 'src/pages/KansasClashSignUp';
// import Login from 'src/pages/public/Login';
// import SignUp from 'src/pages/public/SignUp';

// ----------------------------------------------------------------------

export default function Router() {
  return useRoutes([
    // Auth
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <RegisterPage />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <LoginPage /> },
        { path: 'register-unprotected', element: <RegisterPage /> },
        {
          element: <CompactLayout />,
          children: [
            { path: 'reset-password', element: <ResetPasswordPage /> },
            { path: 'new-password', element: <NewPasswordPage /> },
            { path: 'verify', element: <VerifyCodePage /> },
          ],
        },
      ],
    },

    // Dashboard
    {
      path: 'dashboard',
      element: (
        <GuestGuard>
          <DashboardLayout />
        </GuestGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'app', element: <GeneralAppPage /> },
        { path: 'games', element: <GamesPage /> },
        { path: 'games/:id', element: <GamePage /> },
        { path: 'calendar', element: <CalendarPage /> },
        { path: 'admin', element: <AdminPanel /> },
        { path: 'admin/homepage-uploads', element: <AuthGuard><HomepageUploads /></AuthGuard> },
        { path: 'admin/games/add', element: <AuthGuard><AddGamePage /></AuthGuard> },
        { path: 'admin/games/list', element: <AuthGuard><AdminGamesPage /></AuthGuard> },
        { path: 'admin/events/add', element: <AuthGuard><AddEventPage /></AuthGuard> },
        { path: 'provider/userList', element: <AuthGuard><ProviderUserListPage /></AuthGuard> },
        { path: 'provider/marketing', element: <AuthGuard><MarketingPage /></AuthGuard> },
        { path: 'events', element: <EventsPage /> },
        { path: 'admin/events/list', element: <AuthGuard><AdminEventsPage /></AuthGuard> },
        { path: 'admin/events/registrations', element: <AuthGuard><EventRegistrationsListPage /></AuthGuard> },
        { path: 'myevents', element: <NoIndex><AuthGuard><MyEventsPage /></AuthGuard></NoIndex> },
        { path: 'events/:id', element: <EventPage /> },
        { path: 'addprize', element: <AuthGuard><AddPrizePage /></AuthGuard> },
        { path: 'admin/providers/list', element: <AuthGuard><ProvidersPage /></AuthGuard> },
        { path: 'admin/providers/add', element: <AuthGuard><AddProvider /></AuthGuard> },
        { path: 'admin/gldsproviders/list', element: <AuthGuard><GLDSProvidersPage /></AuthGuard> },
        { path: 'admin/gldsproviders/edit/:id', element: <AuthGuard><AddGLDSProviderPage /></AuthGuard> },
        { path: 'ecommerce', element: <GeneralEcommercePage /> },
        { path: 'analytics', element: <GeneralAnalyticsPage /> },
        { path: 'banking', element: <GeneralBankingPage /> },
        { path: 'booking', element: <GeneralBookingPage /> },
        { path: 'file', element: <GeneralFilePage /> },
        {
          path: 'e-commerce',
          children: [
            { element: <Navigate to="/dashboard/e-commerce/shop" replace />, index: true },
            { path: 'shop', element: <EcommerceShopPage /> },
            { path: 'product/:name', element: <EcommerceProductDetailsPage /> },
            { path: 'list', element: <EcommerceProductListPage /> },
            { path: 'product/new', element: <EcommerceProductCreatePage /> },
            { path: 'product/:name/edit', element: <EcommerceProductEditPage /> },
            { path: 'checkout', element: <EcommerceCheckoutPage /> },
          ],
        },
        {
          path: 'user',
          children: [
            { element: <Navigate to="/dashboard/user/profile" replace />, index: true },
            { path: 'profile', element: <UserProfilePage /> },
            { path: 'cards', element: <UserCardsPage /> },
            { path: 'list', element: <AuthGuard><UserListPage /></AuthGuard> },
            { path: 'new', element: <UserCreatePage /> },
            { path: ':name/edit', element: <UserEditPage /> },
            { path: 'account', element: <AuthGuard><UserAccountPage /></AuthGuard> },
          ],
        },
        {
          path: 'registrations',
          children: [
            { element: <Navigate to="/dashboard/registrations/list" replace />, index: true },
            { path: 'list', element: <AuthGuard><EventRegistrationsListPage /></AuthGuard> },
          ],
        },
        {
          path: 'invoice',
          children: [
            { element: <Navigate to="/dashboard/invoice/list" replace />, index: true },
            { path: 'list', element: <InvoiceListPage /> },
            { path: ':id', element: <InvoiceDetailsPage /> },
            { path: ':id/edit', element: <InvoiceEditPage /> },
            { path: 'new', element: <InvoiceCreatePage /> },
          ],
        },
        {
          path: 'blog',
          children: [
            { element: <Navigate to="/dashboard/blog/posts" replace />, index: true },
            { path: 'posts', element: <BlogPostsPage /> },
            { path: 'post/:title', element: <BlogPostPage /> },
            { path: 'new', element: <BlogNewPostPage /> },
          ],
        },
        { path: 'files-manager', element: <FileManagerPage /> },
        {
          path: 'mail',
          children: [
            { element: <Navigate to="/dashboard/mail/all" replace />, index: true },
            { path: 'label/:customLabel', element: <MailPage /> },
            { path: 'label/:customLabel/:mailId', element: <MailPage /> },
            { path: ':systemLabel', element: <MailPage /> },
            { path: ':systemLabel/:mailId', element: <MailPage /> },
          ],
        },
        {
          path: 'chat',
          children: [
            { element: <ChatPage />, index: true },
            { path: 'new', element: <ChatPage /> },
            { path: ':conversationKey', element: <ChatPage /> },
          ],
        },
        { path: 'calendar', element: <CalendarPage /> },
        { path: 'kanban', element: <KanbanPage /> },
        { path: 'permission-denied', element: <PermissionDeniedPage /> },
        { path: 'blank', element: <BlankPage /> },
      ],
    },

    // Main Routes
    {
      element: <DashboardLayout />,      // <-- add this line
      children: [
        { element: <HomePage />, index: true },
        { path: 'about-us', element: <AboutPage /> },
        { path: 'contact-us', element: <Contact /> },
        { path: 'faqs', element: <FaqsPage /> },
        { path: 'terms-and-conditions', element: <TermsAndConditionsPage /> },
        // Demo Components
        {
          path: 'components',
          children: [
            { element: <ComponentsOverviewPage />, index: true },
            {
              path: 'foundation',
              children: [
                { element: <Navigate to="/components/foundation/colors" replace />, index: true },
                { path: 'colors', element: <FoundationColorsPage /> },
                { path: 'typography', element: <FoundationTypographyPage /> },
                { path: 'shadows', element: <FoundationShadowsPage /> },
                { path: 'grid', element: <FoundationGridPage /> },
                { path: 'icons', element: <FoundationIconsPage /> },
              ],
            },
            {
              path: 'mui',
              children: [
                { element: <Navigate to="/components/mui/accordion" replace />, index: true },
                { path: 'accordion', element: <MUIAccordionPage /> },
                { path: 'alert', element: <MUIAlertPage /> },
                { path: 'autocomplete', element: <MUIAutocompletePage /> },
                { path: 'avatar', element: <MUIAvatarPage /> },
                { path: 'badge', element: <MUIBadgePage /> },
                { path: 'breadcrumbs', element: <MUIBreadcrumbsPage /> },
                { path: 'buttons', element: <MUIButtonsPage /> },
                { path: 'checkbox', element: <MUICheckboxPage /> },
                { path: 'chip', element: <MUIChipPage /> },
                { path: 'data-grid', element: <MUIDataGridPage /> },
                { path: 'dialog', element: <MUIDialogPage /> },
                { path: 'list', element: <MUIListPage /> },
                { path: 'menu', element: <MUIMenuPage /> },
                { path: 'pagination', element: <MUIPaginationPage /> },
                { path: 'pickers', element: <MUIPickersPage /> },
                { path: 'popover', element: <MUIPopoverPage /> },
                { path: 'progress', element: <MUIProgressPage /> },
                { path: 'radio-button', element: <MUIRadioButtonsPage /> },
                { path: 'rating', element: <MUIRatingPage /> },
                { path: 'slider', element: <MUISliderPage /> },
                { path: 'stepper', element: <MUIStepperPage /> },
                { path: 'switch', element: <MUISwitchPage /> },
                { path: 'table', element: <MUITablePage /> },
                { path: 'tabs', element: <MUITabsPage /> },
                { path: 'textfield', element: <MUITextFieldPage /> },
                { path: 'timeline', element: <MUITimelinePage /> },
                { path: 'tooltip', element: <MUITooltipPage /> },
                { path: 'transfer-list', element: <MUITransferListPage /> },
                { path: 'tree-view', element: <MUITreesViewPage /> },
              ],
            },
            {
              path: 'extra',
              children: [
                { element: <Navigate to="/components/extra/animate" replace />, index: true },
                { path: 'animate', element: <DemoAnimatePage /> },
                { path: 'carousel', element: <DemoCarouselsPage /> },
                { path: 'chart', element: <DemoChartsPage /> },
                { path: 'copy-to-clipboard', element: <DemoCopyToClipboardPage /> },
                { path: 'editor', element: <DemoEditorPage /> },
                { path: 'form-validation', element: <DemoFormValidationPage /> },
                { path: 'image', element: <DemoImagePage /> },
                { path: 'label', element: <DemoLabelPage /> },
                { path: 'lightbox', element: <DemoLightboxPage /> },
                { path: 'map', element: <DemoMapPage /> },
                { path: 'mega-menu', element: <DemoMegaMenuPage /> },
                { path: 'multi-language', element: <DemoMultiLanguagePage /> },
                { path: 'navigation-bar', element: <DemoNavigationBarPage /> },
                { path: 'organization-chart', element: <DemoOrganizationalChartPage /> },
                { path: 'scroll', element: <DemoScrollbarPage /> },
                { path: 'snackbar', element: <DemoSnackbarPage /> },
                { path: 'text-max-line', element: <DemoTextMaxLinePage /> },
                { path: 'upload', element: <DemoUploadPage /> },
                { path: 'markdown', element: <DemoMarkdownPage /> },
              ],
            },
          ],
        },
      ],
    },
    {
      element: <SimpleLayout />,
      children: [
        { path: 'payment', element: <PaymentPage /> },
        { path: 'login', element: <Login /> },
        { path: 'oauthlogin', element: <OAuthLogin /> },
        {
          path: 'previeweventdiscord',
          element: (
            <AuthGuard>
              <PreviewEventDiscord />
            </AuthGuard>
          ),
        },
        { path: 'forgotpassword', element: <ForgotPassword /> },
        { path: 'resetpassword', element: <ForgotPassword reset /> },
        { path: 'register', element: <SignUp /> },
        { path: 'verify', element: <SignUp /> },
        { path: 'discord', element: <Discord /> }
      ],
    },
    {
      element: <CompactLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoonPage /> },
        { path: 'maintenance', element: <MaintenancePage /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
      ],
    },
    { path: 'adtran', element: <AdTranLanding /> },
    { path: 'FGNClash', element: <ClashLanding /> },
    { path: 'fgnclash', element: <ClashLanding /> },
    { path: 'clash', element: <ClashLanding /> },
    { path: 'iowaclash', element: <IowaClashLanding /> },
    { path: 'IowaClash', element: <IowaClashLanding /> },
    { path: 'KansasClash', element: <KansasClashLanding /> },
    { path: 'kansasclash', element: <KansasClashLanding /> },
    { path: 'iowaclashfreeagent', element: <IowaClashFreeAgentSignup /> },
    { path: 'IowaClashFreeAgent', element: <IowaClashFreeAgentSignup /> },
    { path: 'kansasclashfreeagent', element: <KansasClashFreeAgentSignup /> },
    { path: 'KansasClashFreeAgent', element: <KansasClashFreeAgentSignup /> },
    { path: 'clashsignup', element: <AuthGuard><ClashSignUp /></AuthGuard> },
    { path: 'kansasclashsignup', element: <AuthGuard><KansasClashSignUp /></AuthGuard> },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}
